/**
 * Find a doula — the member-facing selection flow.
 *
 * She answers six short questions, sees two or three matched doulas, and chooses
 * one. Three things the design deliberately does:
 *
 *   - shows a shortlist, never a browsable directory. A directory produces choice
 *     overload, concentrates demand on whoever sorts first, and exposes capacity
 *     the programme cannot honour;
 *   - says "we've asked her", not "you're matched", because the doula still has to
 *     accept after she picks;
 *   - treats declining all of them as a supported answer rather than a dead end.
 *
 * Ranking happens server-side (services/doula_matching.py), where language is a
 * filter rather than a weight. Nothing here re-sorts what the API returned.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { memberApiFetch, isAuthenticated } from '@/lib/memberAuth';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { ArrowLeft, AlertCircle, Check, Heart, MapPin, Languages, Users } from 'lucide-react';

interface DoulaProfile {
  panel_id: number;
  name: string;
  pronouns?: string;
  bio?: string;
  photo_url?: string;
  languages: string[];
  communities: string[];
  specialisms: string[];
  birth_settings: string[];
  births_supported_band?: string;
  reasons: string[];
  distance_miles?: number;
}

interface ShortlistResponse {
  request_id: number;
  status: string;
  doulas: DoulaProfile[];
  language_fallback?: boolean;
  message?: string;
}

const LANGUAGE_OPTIONS = [
  'English', 'Spanish', 'Cantonese', 'Mandarin', 'Tagalog',
  'Vietnamese', 'Korean', 'Arabic', 'Russian', 'Armenian',
];

const SPECIALISM_OPTIONS = [
  { value: 'first birth', label: 'This is my first birth' },
  { value: 'VBAC', label: 'Birth after a caesarean' },
  { value: 'lactation', label: 'Feeding support' },
  { value: 'teen parent', label: 'Support for young parents' },
  { value: 'perinatal loss', label: 'Support after a loss' },
];

export default function FindADoulaPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<'questions' | 'shortlist' | 'chosen'>('questions');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ShortlistResponse | null>(null);

  // Her answers
  const [languages, setLanguages] = useState<string[]>([]);
  const [noLanguagePreference, setNoLanguagePreference] = useState(false);
  const [community, setCommunity] = useState('');
  const [specialisms, setSpecialisms] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [area, setArea] = useState('');
  const [mattersMost, setMattersMost] = useState('');

  const applyResult = useCallback((data: ShortlistResponse) => {
    setResult(data);
    // 'asked' and 'matched' arrived with the fan-out (TRUS-679). Without them
    // both fall through to the shortlist, so a mother who already has a doula
    // is shown live "Choose" buttons for doulas who were never asked.
    if (data.status === 'selected' || data.status === 'asked' || data.status === 'matched') {
      setStep('chosen');
    } else if (data.doulas.length > 0 || data.status === 'escalated') {
      setStep('shortlist');
    }
  }, []);

  // If she already has an open request, show it rather than starting a second one.
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await memberApiFetch('/api/member-portal/doula/requests/current');
        if (!cancelled && res.ok) {
          applyResult(await res.json());
        }
      } catch {
        // No existing request is the normal case, not an error.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, applyResult]);

  const toggle = (list: string[], value: string, set: (v: string[]) => void) => {
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await memberApiFetch('/api/member-portal/doula/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferred_languages: languages,
          no_language_preference: noLanguagePreference,
          community_preference: community || null,
          requested_specialisms: specialisms,
          due_date: dueDate || null,
          preferred_area: area || null,
          what_matters_most: mattersMost || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      applyResult(await res.json());
    } catch (err: any) {
      setError(t('doula.request_failed', 'We could not send your request. Please try again.'));
      console.error('[FindADoula] request failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const askAll = async () => {
    if (!result?.request_id) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(
        `/api/member-portal/doula/requests/${result.request_id}/ask-all`,
        { method: 'POST' },
      );
      applyResult(await res.json());
    } catch (err) {
      setError(t('doula.ask_all_failed', 'We could not send your request. Please try again.'));
      console.error('[FindADoula] ask-all failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const choose = async (panelId: number) => {
    if (!result) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await memberApiFetch(
        `/api/member-portal/doula/requests/${result.request_id}/select`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ panel_id: panelId }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      applyResult(await res.json());
    } catch (err: any) {
      setError(t('doula.select_failed', 'We could not save your choice. Please try again.'));
      console.error('[FindADoula] selection failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const declineAll = async () => {
    if (!result) return;
    setSubmitting(true);
    try {
      const res = await memberApiFetch(
        `/api/member-portal/doula/requests/${result.request_id}/decline`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: null }),
        },
      );
      if (res.ok) applyResult(await res.json());
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PageState variant="loading" />;

  return (
    <PageContainer>
      <PageHeader
        title={t('doula.find_a_doula', 'Find a doula')}
        description={t(
          'doula.find_a_doula_sub',
          'A doula supports you through pregnancy, birth and the weeks afterwards.',
        )}
        actions={
          <Button variant="ghost" onClick={() => navigate('/member/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back', 'Back')}
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {step === 'questions' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('doula.tell_us', 'Tell us what you are looking for')}</CardTitle>
            <CardDescription>
              {t('doula.six_questions', 'Six quick questions. Nothing here is fixed — you can change it later.')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>{t('doula.language_q', 'Which language would you like your doula to speak?')}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {LANGUAGE_OPTIONS.map((lang) => (
                  <Button
                    key={lang}
                    type="button"
                    size="sm"
                    variant={languages.includes(lang) ? 'default' : 'outline'}
                    onClick={() => toggle(languages, lang, setLanguages)}
                  >
                    {lang}
                  </Button>
                ))}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  id="doula-no-language-preference"
                  type="checkbox"
                  checked={noLanguagePreference}
                  onChange={(e) => setNoLanguagePreference(e.target.checked)}
                />
                {t('doula.no_language_pref', 'I do not mind which language')}
              </label>
            </div>

            <div>
              <Label htmlFor="doula-community">
                {t('doula.community_q', 'Would you like a doula from your own community?')}
              </Label>
              <Input
                id="doula-community"
                value={community}
                onChange={(e) => setCommunity(e.target.value)}
                placeholder={t('doula.community_placeholder', 'Optional')}
                className="mt-2"
              />
            </div>

            <div>
              <Label>{t('doula.support_q', 'What would you most like help with?')}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SPECIALISM_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={specialisms.includes(opt.value) ? 'default' : 'outline'}
                    onClick={() => toggle(specialisms, opt.value, setSpecialisms)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="doula-due-date">{t('doula.due_date_q', 'When is your baby due?')}</Label>
                <Input
                  id="doula-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="doula-area">{t('doula.area_q', 'Which area suits you?')}</Label>
                <Input
                  id="doula-area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder={t('doula.area_placeholder', 'Neighbourhood or postcode')}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="doula-matters">
                {t('doula.matters_q', 'Anything else you would like us to know?')}
              </Label>
              <Textarea
                id="doula-matters"
                value={mattersMost}
                onChange={(e) => setMattersMost(e.target.value)}
                rows={3}
                className="mt-2"
              />
            </div>

            <Button onClick={submit} disabled={submitting} className="w-full sm:w-auto">
              {submitting
                ? t('doula.finding', 'Finding doulas…')
                : t('doula.show_matches', 'Show me my matches')}
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 'shortlist' && result && (
        <div className="space-y-4">
          {result.message && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{result.message}</AlertDescription>
            </Alert>
          )}

          {result.doulas.map((doula) => (
            <Card key={doula.panel_id}>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {doula.name}
                      {doula.pronouns && (
                        <span className="text-sm font-normal text-muted-foreground">
                          ({doula.pronouns})
                        </span>
                      )}
                    </CardTitle>
                    {doula.births_supported_band && (
                      <CardDescription>{doula.births_supported_band}</CardDescription>
                    )}
                  </div>
                  {typeof doula.distance_miles === 'number' && (
                    <Badge variant="outline" className="shrink-0">
                      <MapPin className="mr-1 h-3 w-3" />
                      {Math.round(doula.distance_miles)} mi
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {doula.bio && <p className="text-sm">{doula.bio}</p>}

                <div className="flex flex-wrap gap-2 text-sm">
                  {doula.languages.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Languages className="h-3.5 w-3.5" />
                      {doula.languages.join(', ')}
                    </span>
                  )}
                  {doula.communities.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Users className="h-3.5 w-3.5" />
                      {doula.communities.join(', ')}
                    </span>
                  )}
                </div>

                {doula.reasons.length > 0 && (
                  <ul className="space-y-1">
                    {doula.reasons.map((reason, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <Button onClick={() => choose(doula.panel_id)} disabled={submitting}>
                  <Heart className="mr-2 h-4 w-4" />
                  {t('doula.choose', 'Choose')} {doula.name.split(' ')[0]}
                </Button>
              </CardContent>
            </Card>
          ))}

          {result.doulas.length > 0 && (
            <div className="space-y-2">
              <Button onClick={askAll} disabled={submitting} className="w-full">
                <Heart className="mr-2 h-4 w-4" />
                {t('doula.ask_all', 'Ask all of them')}
              </Button>
              <p className="text-sm text-muted-foreground">
                {t(
                  'doula.ask_all_help',
                  'The first one to say yes will be your doula. This is usually the fastest way.',
                )}
              </p>
              <Button variant="ghost" onClick={declineAll} disabled={submitting}>
                {t('doula.none_suit', 'None of these feel right')}
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'chosen' && result && (
        <Card>
          <CardHeader>
            <CardTitle>
              {result.status === 'matched'
                ? t('doula.you_have_a_doula', 'You have a doula')
                : result.status === 'asked'
                  ? t('doula.asked_them', 'We have asked them')
                  : t('doula.asked_her', 'We have asked her')}
            </CardTitle>
            <CardDescription>
              {result.message ||
                t('doula.hear_soon', 'You will hear as soon as she confirms.')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {result.doulas.map((doula) => (
              <div key={doula.panel_id} className="space-y-2">
                <p className="font-medium">{doula.name}</p>
                {doula.bio && <p className="text-sm text-muted-foreground">{doula.bio}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
